import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  GlobeAltIcon,
  BanknotesIcon,
  CogIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const CompanyManagement = () => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [showBankModal, setShowBankModal] = useState(false);
  const [editingBankIndex, setEditingBankIndex] = useState(null);
  const [bankFormData, setBankFormData] = useState({
    bankName: '',
    swiftCode: '',
    iban: '',
    isDefault: false
  });

  const defaultFormData = {
    name: '',
    address: '',
    city: '',
    country: 'Bulgaria',
    taxId: '',
    vatTaxId: '',
    logo: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    materiallyResponsiblePerson: {
      name: ''
    },
    bankAccounts: [],
    invoiceSettings: {
      currency: 'BGN',
      taxRate: 20,
      paymentTerms: 30,
      template: 'default',
      invoicePrefix: 'INV-2024-'
    }
  };

  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      console.log('Fetching companies from API...');
      
      const response = await fetch('/api/finance/companies', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Companies data received:', data);
        console.log('Number of companies:', data.length);
        setCompanies(data);
      } else if (response.status === 404) {
        console.log('No companies found (404)');
        setCompanies([]);
      } else {
        throw new Error('Failed to fetch companies');
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setMessage({ type: 'error', text: t('companyManagement.failedToLoadCompanies') });
    } finally {
      setLoading(false);
    }
  };

  const openModal = (company = null) => {
    if (company) {
      setEditingCompany(company);
      console.log('Opening modal for company:', company);
      console.log('Company taxId:', company.taxId, 'Company vatTaxId:', company.vatTaxId);
      
      // Handle migration from old bankDetails to new bankAccounts structure
      let bankAccounts = company.bankAccounts || [];
      
      if (bankAccounts.length === 0 && company.bankDetails) {
        const oldBankDetails = company.bankDetails;
        if (oldBankDetails.bankName || oldBankDetails.swiftCode || oldBankDetails.accountNumber) {
          bankAccounts = [{
            bankName: oldBankDetails.bankName || '',
            swiftCode: oldBankDetails.swiftCode || '',
            iban: oldBankDetails.accountNumber || '',
            isDefault: true
          }];
        }
      }

      const formDataToSet = {
        ...defaultFormData,
        ...company,
        vatTaxId: company.vatTaxId || '',
        materiallyResponsiblePerson: {
          name: company.materiallyResponsiblePerson?.name || ''
        },
        bankAccounts: bankAccounts,
        invoiceSettings: {
          ...defaultFormData.invoiceSettings,
          ...company.invoiceSettings
        }
      };
      
      console.log('Setting form data:', formDataToSet);
      setFormData(formDataToSet);
    } else {
      setEditingCompany(null);
      setFormData(defaultFormData);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCompany(null);
    setFormData(defaultFormData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        const [section, field] = parts;
        setFormData(prev => ({
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value
          }
        }));
      } else if (parts.length === 3) {
        const [section, subsection, field] = parts;
        setFormData(prev => ({
          ...prev,
          [section]: {
            ...prev[section],
            [subsection]: {
              ...prev[section]?.[subsection],
              [field]: value
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Bank Account Management Functions
  const openBankModal = (index = null) => {
    if (index !== null) {
      const bank = formData.bankAccounts[index];
      setBankFormData({
        bankName: bank.bankName,
        swiftCode: bank.swiftCode,
        iban: bank.iban,
        isDefault: bank.isDefault
      });
      setEditingBankIndex(index);
    } else {
      setBankFormData({
        bankName: '',
        swiftCode: '',
        iban: '',
        isDefault: false
      });
      setEditingBankIndex(null);
    }
    setShowBankModal(true);
  };

  const closeBankModal = () => {
    setShowBankModal(false);
    setEditingBankIndex(null);
    setBankFormData({
      bankName: '',
      swiftCode: '',
      iban: '',
      isDefault: false
    });
  };

  const handleBankInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    console.log('Bank form input changed:', name, newValue);
    setBankFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleBankSubmit = (e) => {
    e.preventDefault();
    
    if (!bankFormData.bankName || !bankFormData.swiftCode || !bankFormData.iban) {
      setMessage({ type: 'error', text: t('companyManagement.pleaseFillAllRequiredFields') });
      return;
    }

    const updatedBankAccounts = [...formData.bankAccounts];
    
    if (editingBankIndex !== null) {
      // Updating existing bank account
      updatedBankAccounts[editingBankIndex] = { ...bankFormData };
    } else {
      // Adding new bank account
      updatedBankAccounts.push({ ...bankFormData });
    }

    // If this bank account is set as default, unset all others
    if (bankFormData.isDefault) {
      updatedBankAccounts.forEach((bank, index) => {
        if (editingBankIndex !== null) {
          // When editing, only unset others, not the current one
          if (index !== editingBankIndex) {
            bank.isDefault = false;
          }
        } else {
          // When adding new, unset all existing ones
          bank.isDefault = false;
        }
      });
      
      // Set the current bank account as default
      if (editingBankIndex !== null) {
        updatedBankAccounts[editingBankIndex].isDefault = true;
      } else {
        updatedBankAccounts[updatedBankAccounts.length - 1].isDefault = true;
      }
    }

    console.log('Updated bank accounts:', updatedBankAccounts);

    setFormData(prev => ({
      ...prev,
      bankAccounts: updatedBankAccounts
    }));

    closeBankModal();
    setMessage({ type: 'success', text: t('companyManagement.bankAccountUpdatedSuccessfully', { action: editingBankIndex !== null ? t('companyManagement.updated') : t('companyManagement.added') }) });
  };

  const deleteBankAccount = (index) => {
    if (window.confirm(t('companyManagement.confirmDeleteBankAccount'))) {
      const updatedBankAccounts = formData.bankAccounts.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        bankAccounts: updatedBankAccounts
      }));
      setMessage({ type: 'success', text: t('companyManagement.bankAccountDeletedSuccessfully') });
    }
  };

  const setDefaultBankAccount = (index) => {
    const updatedBankAccounts = formData.bankAccounts.map((bank, i) => ({
      ...bank,
      isDefault: i === index
    }));
    setFormData(prev => ({
      ...prev,
      bankAccounts: updatedBankAccounts
    }));
    setMessage({ type: 'success', text: t('companyManagement.defaultBankAccountUpdatedSuccessfully') });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Frontend validation - at least one tax ID must be provided
    const hasTaxId = formData.taxId && formData.taxId.trim() !== '';
    const hasVatTaxId = formData.vatTaxId && formData.vatTaxId.trim() !== '';
    
    console.log('Company validation - taxId:', formData.taxId, 'vatTaxId:', formData.vatTaxId);
    console.log('Has taxId:', hasTaxId, 'Has vatTaxId:', hasVatTaxId);
    
    if (!hasTaxId && !hasVatTaxId) {
      setMessage({ type: 'error', text: t('companyManagement.eitherTaxIdOrVatTaxIdRequired') });
      return;
    }
    
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('jwtToken');
      const url = editingCompany 
        ? `/api/finance/companies/${editingCompany._id}`
        : '/api/finance/companies';
      
      console.log('Saving company with bank accounts:', formData.bankAccounts);
      console.log('Sending formData to backend:', formData);
      
      const response = await fetch(url, {
        method: editingCompany ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        await fetchCompanies(); // Refresh the list
        closeModal();
        setMessage({ type: 'success', text: t('companyManagement.companySavedSuccessfully', { action: editingCompany ? t('companyManagement.updated') : t('companyManagement.created') }) });
      } else {
        const errorData = await response.json();
        console.log('Error response data:', errorData);
        throw new Error(errorData.message || t('companyManagement.failedToSaveCompany'));
      }
    } catch (error) {
      console.error('Error saving company:', error);
      setMessage({ type: 'error', text: error.message || t('companyManagement.failedToSaveCompany') });
    } finally {
      setSaving(false);
    }
  };

  const deleteCompany = async (companyId) => {
    if (window.confirm(t('companyManagement.confirmDeleteCompany'))) {
      try {
        const token = localStorage.getItem('jwtToken');
        const response = await fetch(`/api/finance/companies/${companyId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          await fetchCompanies();
          setMessage({ type: 'success', text: t('companyManagement.companyDeletedSuccessfully') });
        } else {
          throw new Error(t('companyManagement.failedToDeleteCompany'));
        }
      } catch (error) {
        console.error('Error deleting company:', error);
        setMessage({ type: 'error', text: t('companyManagement.failedToDeleteCompany') });
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">{t('companyManagement.loadingCompanies')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-full mx-auto">
              {/* Header */}
        <div className="flex items-center justify-between mb-6 px-4 sm:px-6 lg:px-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('companyManagement.companies')}</h1>
          <p className="text-gray-600">{t('companyManagement.manageCompanyInformation')}</p>
        </div>
        <button
          onClick={() => openModal()}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
        >
          <PlusIcon className="h-4 w-4" />
          <span>{t('companyManagement.addCompany')}</span>
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircleIcon className="h-5 w-5 text-green-600" />
          ) : (
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          )}
          <span>{message.text}</span>
        </div>
      )}

              {/* Companies List */}
        <div className="bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600">{t('companyManagement.loadingCompanies')}</p>
          </div>
        ) : companies.length === 0 ? (
          <div className="p-8 text-center">
            <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">{t('companyManagement.noCompaniesFound')}</p>
            <button
              onClick={() => openModal()}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              {t('companyManagement.addYourFirstCompany')}
            </button>
          </div>
        ) : (
          <>
                          <div className="px-6 py-4 border-b border-gray-200">
                <span className="text-sm text-gray-600">
                  {companies.length} {companies.length === 1 ? t('companyManagement.companyFound') : t('companyManagement.companiesFound')}
                </span>
              </div>
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('companyManagement.company')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('companyManagement.contact')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('companyManagement.location')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('companyManagement.mrp')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('companyManagement.bankAccounts')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('companyManagement.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {companies.map((company) => (
                  <tr key={company._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {company.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {t('companyManagement.taxId')}: {company.taxId}
                        </div>
                        {company.vatTaxId && (
                          <div className="text-sm text-gray-500">
                            {t('companyManagement.vatTaxId')}: BG{company.vatTaxId}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">
                          {company.contactEmail}
                        </div>
                        <div className="text-sm text-gray-500">
                          {company.contactPhone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {company.city}, {company.country}
                      </div>
                      <div className="text-sm text-gray-500">
                        {company.address}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {company.materiallyResponsiblePerson?.name || t('companyManagement.notSpecified')}
                      </div>
                    </td>
                                          <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {company.bankAccounts?.length || 0} {(company.bankAccounts?.length || 0) === 1 ? t('companyManagement.bankAccount') : t('companyManagement.bankAccounts')}
                        </div>
                      {company.bankAccounts?.length > 0 && (
                        <div className="text-sm text-gray-500">
                          {company.bankAccounts.find(acc => acc.isDefault) ? t('companyManagement.defaultSet') : t('companyManagement.noDefault')}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openModal(company)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteCompany(company._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Company Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingCompany ? t('companyManagement.editCompany') : t('companyManagement.addNewCompany')}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">{t('companyManagement.companyInformation')}</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.companyName')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.taxIdRequired')}
                    </label>
                    <input
                      type="text"
                      name="taxId"
                      value={formData.taxId}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.vatTaxIdRequired')}
                    </label>
                    <input
                      type="text"
                      name="vatTaxId"
                      value={formData.vatTaxId}
                      onChange={handleInputChange}
                      placeholder="BG123456789"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.city')} *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.address')} *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.mrpName')}
                    </label>
                    <input
                      type="text"
                      name="materiallyResponsiblePerson.name"
                      value={formData.materiallyResponsiblePerson.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.country')} *
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <EnvelopeIcon className="h-6 w-6 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900">{t('companyManagement.contactInformation')}</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.contactEmail')} *
                    </label>
                    <input
                      type="email"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.contactPhone')} *
                    </label>
                    <input
                      type="tel"
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.website')}
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.logoUrl')}
                    </label>
                    <input
                      type="url"
                      name="logo"
                      value={formData.logo}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Bank Accounts */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <BanknotesIcon className="h-6 w-6 text-yellow-600" />
                    <h3 className="text-lg font-semibold text-gray-900">{t('companyManagement.bankAccounts')}</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => openBankModal()}
                    className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2 text-sm"
                  >
                    <PlusIcon className="h-3 w-3" />
                    <span>{t('companyManagement.addBank')}</span>
                  </button>
                </div>
                
                {formData.bankAccounts.length === 0 ? (
                  <div className="text-center py-4">
                    <BanknotesIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600 text-sm">{t('companyManagement.noBankAccountsAdded')}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.bankAccounts.map((bank, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white rounded border">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{bank.bankName}</div>
                          <div className="text-xs text-gray-500">{bank.swiftCode} • {bank.iban}</div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {bank.isDefault && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {t('companyManagement.default')}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => openBankModal(index)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <PencilIcon className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteBankAccount(index)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Invoice Settings */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <CogIcon className="h-6 w-6 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">{t('companyManagement.invoiceSettings')}</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.defaultCurrency')}
                    </label>
                    <select
                      name="invoiceSettings.currency"
                      value={formData.invoiceSettings.currency}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="BGN">{t('companyManagement.bgnBulgarianLev')}</option>
                      <option value="EUR">{t('companyManagement.eurEuro')}</option>
                      <option value="USD">{t('companyManagement.usdUsDollar')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.defaultTaxRate')} ({t('companyManagement.percent')})
                    </label>
                    <input
                      type="number"
                      name="invoiceSettings.taxRate"
                      value={formData.invoiceSettings.taxRate}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.paymentTerms')} ({t('companyManagement.days')})
                    </label>
                    <input
                      type="number"
                      name="invoiceSettings.paymentTerms"
                      value={formData.invoiceSettings.paymentTerms}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('companyManagement.invoiceNumberPrefix')}
                    </label>
                    <input
                      type="text"
                      name="invoiceSettings.invoicePrefix"
                      value={formData.invoiceSettings.invoicePrefix}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                >
                  {t('companyManagement.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>{t('companyManagement.saving')}</span>
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4" />
                      <span>{editingCompany ? t('companyManagement.updateCompany') : t('companyManagement.createCompany')}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>

      {/* Bank Account Modal */}
      {showBankModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingBankIndex !== null ? t('companyManagement.editBankAccount') : t('companyManagement.addBankAccount')}
                </h3>
                <button
                  onClick={closeBankModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={handleBankSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companyManagement.bankName')} *
                  </label>
                  <input
                    type="text"
                    name="bankName"
                    value={bankFormData.bankName}
                    onChange={handleBankInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companyManagement.swiftCode')} *
                  </label>
                  <input
                    type="text"
                    name="swiftCode"
                    value={bankFormData.swiftCode}
                    onChange={handleBankInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('companyManagement.iban')} *
                  </label>
                  <input
                    type="text"
                    name="iban"
                    value={bankFormData.iban}
                    onChange={handleBankInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isDefault"
                    checked={bankFormData.isDefault}
                    onChange={handleBankInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    {t('companyManagement.setAsDefaultBankAccount')}
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeBankModal}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                  >
                    {t('companyManagement.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    {editingBankIndex !== null ? t('companyManagement.update') : t('companyManagement.add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyManagement; 